import { store } from '../context/store';
import {
  QuestionAttemptStatus,
  QuestionAttemptStatusType,
} from '../types/assignmentTypes';
import {
  ITQBadge,
  ITQBadges,
  ITQConnectConfig,
  ITQRatingCategories,
  ITQRatingCategory,
  TQRatingCategoryValue,
} from '../types/tqConnectTypes';
import {
  ITQProfileCompletionQueryWithResponse,
  TQConnectUserAccessType,
} from '../types/tqConnectTypes/user';

/**
 * Checks if TQ Connect is enabled for the current logged in user. It checks if the host has tq connect config enabled and if the user has community creator access or tq connect access as customer (if has any mango enabled for tq connect and has a valid score).
 * @returns an object with the following properties:
 *  - isTQConnectEnabled: True if TQ Connect is enabled for the current user, false otherwise.
 *  - canAccessTQConnectAsCreator: True if the user has community creator access, false otherwise.
 *  - canAccessTQConnectAsCustomer: True if the user has tq connect access as customer (if has any mango enabled for tq connect and has a valid score), false otherwise.
 *  - canViewTQConnectAsNonSubscriber: True if TQ Connect feature is active but user doesn't have access (for showing unlock prompt screen).
 *  - userAccessType: The type of user that has access to TQ Connect, 'creator' or 'customer' or null.
 */
export const checkTQConnectAccess = (): {
  isTQConnectEnabled: boolean;
  canAccessTQConnectAsCreator: boolean;
  canAccessTQConnectAsCustomer: boolean;
  canViewTQConnectAsNonSubscriber: boolean;
  userAccessType: TQConnectUserAccessType | null;
} => {
  const { hostMetadata } = store.getState().app;
  const {
    userCommunityAccess: {
      // check if user has community creator access
      hasCommunityCreatorAccess,
      // check if user has tq connect access as customer (if has any mango enabled for tq connect)
      hasTQConnectAccessForUser,
    },
  } = store.getState().community;

  const canAccessTQConnectAsCreator = hasCommunityCreatorAccess;
  const canAccessTQConnectAsCustomer = hasTQConnectAccessForUser;
  // hasTQConnectAccessForUser && checkIfCurrentUserHasValidScore('tq-score');

  const isTQConnectFeatureActive =
    Boolean(hostMetadata?.isTQConnectFeatureActive) &&
    Boolean(hostMetadata?.tqConnectConfig?.enabled);

  const isTQConnectEnabled =
    isTQConnectFeatureActive &&
    (canAccessTQConnectAsCreator || canAccessTQConnectAsCustomer);

  // User can view TQ Connect as non-subscriber if the feature is active but they don't have access
  const canViewTQConnectAsNonSubscriber =
    isTQConnectFeatureActive &&
    !canAccessTQConnectAsCreator &&
    !canAccessTQConnectAsCustomer;

  return {
    isTQConnectEnabled,
    canAccessTQConnectAsCreator,
    canAccessTQConnectAsCustomer,
    canViewTQConnectAsNonSubscriber,
    userAccessType:
      (canAccessTQConnectAsCreator && 'creator') ||
      (canAccessTQConnectAsCustomer && 'customer') ||
      null,
  };
};

export const getCurrentBadge = (badges: ITQBadges, score: number): ITQBadge => {
  // make a list of badge thresholds in ascending order (lowest to highest score)
  const badgeThresholds = Object.keys(badges)
    .map(Number)
    .sort((a, b) => a - b);

  const currentBadge =
    badgeThresholds
      // filter the badge thresholds to only include the ones that are less than or equal to the given score
      .filter((threshold) => score <= threshold)
      // make a list of badges from the badge thresholds
      // and return the first one (max level badge for the given score)
      .map((threshold) => badges[String(threshold)])[0] ||
    // if no badge is found for the given score, return the lowest level badge from the original badges list
    badges[String(badgeThresholds[0])];

  return currentBadge;
};

export const getTQProfileQueryAttemptStatus = (
  question: ITQProfileCompletionQueryWithResponse,
): QuestionAttemptStatusType => {
  const { tqConnectProfileCompletionResponses } = store.getState().user;

  const answer = tqConnectProfileCompletionResponses?.find(
    (q) => q.questionId === question?._id,
  );

  const { selectedOptions, subjectiveAnswer } = answer || {};

  if (selectedOptions?.length || subjectiveAnswer?.trim()) {
    return QuestionAttemptStatus.ATTEMPTED;
  }

  return QuestionAttemptStatus.SKIPPED;
};

export const getTQServiceRequestReviewRating = (
  rating: TQRatingCategoryValue,
  tqConnectConfig?: ITQConnectConfig,
): ITQRatingCategory | null => {
  const ratingCategories = (tqConnectConfig?.ratingCategories ||
    {}) as ITQRatingCategories;

  return ratingCategories[rating] || null;
};
